import {useCallback, useEffect, useMemo, useState} from 'react';

import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {getLicense} from 'src/openapi/admin/apis/GetLicenseApi';
import {updateLicenseBaseInfo} from 'src/openapi/admin/apis/UpdateLicenseBaseInfoApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {LicenseInfo} from 'src/openapi/admin/models/LicenseInfo';

type Props = {
    licenseId: number,
};

export const useLicenseInformation = ({licenseId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {isFutureDate} = useDateTime();

    const [licenseInformationLoading, setLicenseInformationLoading] = useState(false);
    const [initialLicenseInformation, setInitialLicenseInformation] = useState<LicenseInfo | null>(null);
    const [licenseInformation, setLicenseInformation] = useState<LicenseInfo | null>(null);

    useEffect(() => {
        if (licenseId) {
            setLicenseInformationLoading(true);
            apiDispatch(getLicense({licenseId: +licenseId})).then(response => {
                if (response.payload.success) {
                    setLicenseInformation(response.payload.data);
                    setInitialLicenseInformation(response.payload.data);
                }

                setLicenseInformationLoading(false);
            });
        }
    }, [licenseId, apiDispatch]);

    const onSaveLicenseInformation = useCallback(async () => {
        let error: string | null = null;
        if (licenseInformation) {
            const {
                typeCode,
                startDate,
                endDate,
                concurrentUserCount,
                contractId,
            } = licenseInformation;

            if (startDate && endDate) {
                const saveLicenseResponse = await apiDispatch(updateLicenseBaseInfo({
                    licenseUpdateRequest: {
                        licenseType: typeCode,
                        startDate,
                        endDate,
                        concurrentUserCount,
                        contractId,
                    },
                    licenseId,
                }));

                if (saveLicenseResponse.payload.error) {
                    error = getError({error: saveLicenseResponse.payload.data, separator: ' '});
                    setLicenseInformation(initialLicenseInformation);
                } else {
                    setInitialLicenseInformation(licenseInformation);
                }
            }
        }

        return error;
    }, [licenseInformation, initialLicenseInformation, apiDispatch, licenseId]);

    const onDiscard = useCallback(() => {
        if (initialLicenseInformation) {
            setLicenseInformation(initialLicenseInformation);
        }
    }, [initialLicenseInformation]);

    const isInitialLicenseInformation = useMemo(() => (licenseInformation
        ? isDeepEqual({prev: initialLicenseInformation, current: licenseInformation})
        : true
    ), [initialLicenseInformation, licenseInformation]);

    const isExpiredLicense = useMemo(() => (licenseInformation
        ? !licenseInformation.isActive && !isFutureDate(licenseInformation?.startDate)
        : true
    ), [licenseInformation, isFutureDate]);

    return {
        licenseInformationLoading,
        initialLicenseInformation,
        licenseInformation,
        isExpiredLicense,
        isInitialLicenseInformation,
        setLicenseInformation,
        onSaveLicenseInformation,
        onDiscard,
    };
};
